import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import VendorStatusBadge from "./VendorStatusBadge";

function Field({ field, value, onChange, required }) {
  const commonProps = {
    id: field.id,
    name: field.id,
    required,
    onChange,
  };

  if (field.type === "select") {
    return (
      <select {...commonProps} className="form-select" value={value || ""}>
        <option value="">Select...</option>
        {(field.options || []).map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    );
  }

  if (field.type === "radio") {
    return (
      <div role="radiogroup" aria-labelledby={`${field.id}-label`} className="d-flex flex-wrap gap-3">
        {(field.options || []).map((opt) => (
          <label key={opt} className="form-check">
            <input
              type="radio"
              name={field.id}
              value={opt}
              checked={value === opt}
              onChange={(e) => onChange(e)}
              className="form-check-input"
            />
            <span className="form-check-label ms-1">{opt}</span>
          </label>
        ))}
      </div>
    );
  }

  if (field.type === "textarea") {
    return (
      <textarea
        {...commonProps}
        rows={field.rows || 3}
        placeholder={field.placeholder || ""}
        value={value || ""}
        className="form-control"
      />
    );
  }

  if (field.type === "file") {
    return (
      <input
        type="file"
        id={field.id}
        name={field.id}
        multiple={Boolean(field.multiple)}
        className="form-control"
        onChange={(e) => {
          const names = Array.from(e.target.files || []).map((f) => f.name);
          onChange({
            target: {
              name: field.id,
              value: names,
            },
          });
        }}
      />
    );
  }

  return (
    <input
      {...commonProps}
      type={field.type === "number" ? "number" : field.type === "date" ? "date" : "text"}
      step={field.step || undefined}
      placeholder={field.placeholder || ""}
      value={value || ""}
      className="form-control"
    />
  );
}

function getFieldHelpText(field) {
  if (field.helpText) return field.helpText;
  if (field.placeholder) return field.placeholder;
  return `Provide ${field.label.toLowerCase()}.`;
}

function isFilled(value) {
  if (Array.isArray(value)) return value.length > 0;
  return value !== undefined && value !== null && value !== "";
}

function getRequiredFieldIds(templates, selectedCategory, answers) {
  if (!templates || !selectedCategory) return [];

  const required = new Set();
  const methodType = answers.methodType;

  templates.universalFields
    .filter((f) => f.required)
    .forEach((f) => required.add(f.id));

  selectedCategory.questions
    .filter((f) => f.required)
    .forEach((f) => required.add(f.id));

  const methodSpecific = selectedCategory.methodRequiredFields?.[methodType] || [];
  methodSpecific.forEach((fieldId) => required.add(fieldId));

  (selectedCategory.conditionalRequired || []).forEach((rule) => {
    if (answers?.[rule?.when?.field] === rule?.when?.equals) {
      (rule.fields || []).forEach((fieldId) => required.add(fieldId));
    }
  });

  return Array.from(required);
}

function vendorAuthHeaders() {
  const t = localStorage.getItem("vendorAccessToken");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

/**
 * @param {object} props
 * @param {number} [props.assignmentId] — when set, saves to assignment APIs and locks category
 * @param {function} [props.onSaved] — optional callback after save/submit
 * @param {boolean} [props.useVendorAuth] — use vendor JWT + /api/vendor/* (vendor portal); otherwise admin APIs
 */
export default function VendorScope3Form({ assignmentId, onSaved, useVendorAuth = false }) {
  const [templates, setTemplates] = useState(null);
  const [categoryId, setCategoryId] = useState(1);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [assignmentMeta, setAssignmentMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const assignmentMode = Boolean(assignmentId);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const url = useVendorAuth ? "/api/vendor/templates" : "/api/scope3/templates";
        const { data } = await axios.get(url, useVendorAuth ? { headers: vendorAuthHeaders() } : undefined);
        if (cancelled) return;
        setTemplates(data);
        if (!assignmentMode) {
          setAnswers({ reportingYear: data?.metadata?.reportingYearDefault || "FY2026" });
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.message || err.message || "Failed to load templates");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [assignmentMode, useVendorAuth]);

  useEffect(() => {
    if (!assignmentId || !templates) return;
    let cancelled = false;
    (async () => {
      try {
        const path = useVendorAuth
          ? `/api/vendor/assignments/${assignmentId}`
          : `/api/vendors/assignments/${assignmentId}`;
        const { data } = await axios.get(path, useVendorAuth ? { headers: vendorAuthHeaders() } : undefined);
        if (cancelled) return;
        const a = data.assignment;
        setAssignmentMeta(a);
        setCategoryId(a.scope3Category);
        const saved = data.response?.responseJson || {};
        setAnswers({
          ...saved,
          reportingYear: saved.reportingYear || templates.metadata?.reportingYearDefault || "FY2026",
        });
        setError("");
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.message || err.message || "Failed to load assignment");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [assignmentId, templates, useVendorAuth]);

  const selectedCategory = useMemo(() => {
    return templates?.categories?.find((c) => Number(c.id) === Number(categoryId));
  }, [templates, categoryId]);

  const renderedFields = useMemo(() => {
    if (!templates || !selectedCategory) return [];
    return [...templates.universalFields, ...selectedCategory.questions];
  }, [templates, selectedCategory]);

  const requiredFieldIds = useMemo(() => {
    return getRequiredFieldIds(templates, selectedCategory, answers);
  }, [templates, selectedCategory, answers]);

  const requiredMissing = useMemo(() => {
    return requiredFieldIds.filter((fieldId) => !isFilled(answers[fieldId]));
  }, [requiredFieldIds, answers]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setAnswers((prev) => ({ ...prev, [name]: value }));
  };

  const saveDraft = async () => {
    if (!assignmentId) return;
    setSaving(true);
    setError("");
    try {
      const path = useVendorAuth
        ? `/api/vendor/assignments/${assignmentId}/response`
        : `/api/vendors/assignments/${assignmentId}/response`;
      await axios.put(
        path,
        { responseJson: answers },
        useVendorAuth ? { headers: vendorAuthHeaders() } : undefined
      );
      if (onSaved) onSaved();
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const submitFinal = async () => {
    if (!assignmentId) return;
    if (requiredMissing.length > 0) {
      setError(`Missing required fields: ${requiredMissing.join(", ")}`);
      return;
    }
    setSaving(true);
    setError("");
    try {
      const path = useVendorAuth
        ? `/api/vendor/assignments/${assignmentId}/submit`
        : `/api/vendors/assignments/${assignmentId}/submit`;
      await axios.post(
        path,
        { responseJson: answers },
        useVendorAuth ? { headers: vendorAuthHeaders() } : undefined
      );
      if (onSaved) onSaved();
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Submit failed");
    } finally {
      setSaving(false);
    }
  };

  const runCalculate = async (e) => {
    e?.preventDefault();
    setError("");
    setResult(null);

    if (!assignmentMode) {
      if (requiredMissing.length > 0) {
        setError(`Missing required fields: ${requiredMissing.join(", ")}`);
        return;
      }
    } else if (requiredMissing.length > 0) {
      setError(`Missing required fields before calculation: ${requiredMissing.join(", ")}`);
      return;
    }

    setLoading(true);
    try {
      if (assignmentMode) {
        const { data } = await axios.post(`/api/vendors/assignments/${assignmentId}/calculate`);
        setResult(data.result);
      } else {
        const { data } = await axios.post("/api/scope3/calculate", {
          categoryId: Number(categoryId),
          answers,
        });
        setResult(data);
      }
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Calculation failed";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  if (!templates) {
    return (
      <div className="p-3 text-muted">
        {error ? <span className="text-danger">{error}</span> : "Loading templates…"}
      </div>
    );
  }

  return (
    <div className="py-1" style={{ maxWidth: 980 }}>
      {assignmentMode && assignmentMeta ? (
        <p className="small text-muted mb-3 d-flex flex-wrap align-items-center gap-2">
          <span>
            Reporting year <strong>{assignmentMeta.reportingYear}</strong>
          </span>
          <span className="text-muted">·</span>
          <span className="d-inline-flex align-items-center gap-2">
            <span>Status</span>
            <VendorStatusBadge status={assignmentMeta.status} variant="inline" />
          </span>
        </p>
      ) : null}

      <p className="text-muted small mb-3">
        Complete the required fields, then save or calculate. Field help is on the <span title="Help">?</span> icons.
      </p>

      {!assignmentMode ? (
        <div className="mb-3">
          <label htmlFor="vendor-categoryId" className="form-label fw-semibold">
            Category
          </label>
          <select
            id="vendor-categoryId"
            className="form-select"
            value={categoryId}
            onChange={(e) => {
              setCategoryId(Number(e.target.value));
              setResult(null);
              setError("");
            }}
          >
            {templates.categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.code} — {cat.name}
              </option>
            ))}
          </select>
        </div>
      ) : (
        <div className="mb-3">
          <span className="fw-semibold">Category: </span>
          <span>
            {selectedCategory?.code} — {selectedCategory?.name}
          </span>
        </div>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!assignmentMode) runCalculate(e);
        }}
      >
        {renderedFields.map((field) => (
          <div key={field.id} className="mb-3 p-3 border rounded bg-light bg-opacity-50">
            <label
              id={`${field.id}-label`}
              htmlFor={field.id}
              className="form-label fw-semibold d-flex align-items-center gap-2"
            >
              <span>
                {field.label}
                {requiredFieldIds.includes(field.id) ? " *" : ""}
              </span>
              <span
                title={getFieldHelpText(field)}
                aria-label={`Help: ${field.label}`}
                className="d-inline-flex align-items-center justify-content-center rounded-circle border text-secondary small"
                style={{
                  width: 22,
                  height: 22,
                  cursor: "help",
                  userSelect: "none",
                  fontSize: 12,
                }}
              >
                ?
              </span>
            </label>
            <Field
              field={field}
              value={answers[field.id]}
              onChange={handleInputChange}
              required={requiredFieldIds.includes(field.id)}
            />
          </div>
        ))}

        <div className="d-flex flex-wrap gap-2 mt-3">
          {assignmentMode ? (
            <>
              <button type="button" className="btn btn-outline-secondary" disabled={saving} onClick={saveDraft}>
                {saving ? "Saving…" : "Save draft"}
              </button>
              <button type="button" className="btn btn-outline-primary" disabled={saving} onClick={submitFinal}>
                Submit
              </button>
              {!useVendorAuth ? (
                <button type="button" className="btn btn-success" disabled={loading} onClick={runCalculate}>
                  {loading ? "Calculating…" : "Calculate emissions"}
                </button>
              ) : null}
            </>
          ) : (
            <button type="submit" className="btn btn-success" disabled={loading}>
              {loading ? "Calculating…" : "Calculate emissions"}
            </button>
          )}
        </div>
      </form>

      {error ? (
        <div className="alert alert-danger mt-3" role="alert">
          {error}
        </div>
      ) : null}

      {result ? (
        <div className="mt-4 p-3 border rounded bg-white">
          <h3 className="h5">Result</h3>
          <p className="mb-1">
            <strong>Category:</strong> {result.categoryId}
          </p>
          <p className="mb-1">
            <strong>Factor used:</strong> {result.selectedFactorId || "—"}
          </p>
          <p className="mb-1">
            <strong>CO₂e:</strong> {result.estimate?.co2e} {result.estimate?.co2e_unit}
          </p>
          <details className="mt-2">
            <summary className="small text-muted">Technical details</summary>
            <pre className="small mt-2 mb-0 p-2 bg-light rounded overflow-auto" style={{ maxHeight: 320 }}>
              {JSON.stringify(result, null, 2)}
            </pre>
          </details>
        </div>
      ) : null}
    </div>
  );
}
